import { Typography, Button, Box, Grid } from '@material-ui/core'
import React, { Dispatch, useState, useEffect } from 'react'
import { SubGroupRequestDTO, SubGroupsClient, SubGroupUpdateRequestDTO, OrganizationsClient, SubGroupResponseDTO } from '../generated/ApiClient'
import { Formik, Form, Field } from 'formik'
import { TextField } from 'formik-material-ui'
import * as Yup from 'yup'
import { useDispatch } from "react-redux";
import { actionCreators as snackbar } from '../store/AlertSnackbarStore'
import { RouteComponentProps, useHistory } from 'react-router'
import { History } from 'history'
import CenteredProgress from '../common/CenteredLoader'
import { utcToLocal } from '../common/Shared'
import { DateTime } from 'luxon'
import { Admin, useIsUserInRoles } from '../hooks/useIsUserInRoles';

const SubgroupDetailsFormSchema = Yup.object().shape({
    name: Yup.string().max(100).required(),
})

type SubgroupProps = { organizationId: string; subgroupId: string | undefined; } | RouteComponentProps<{ organizationId: string; subgroupId: string | undefined; }>
type SubgroupFormProperties = Yup.InferType<typeof SubgroupDetailsFormSchema>

async function formSubmitCallback(name: string, subgroupId: string | undefined, organizationId: string, dispatch: Dispatch<any>, history: History<any>) {
    try {
        if (subgroupId) {
            const requestData = { name };
            await new SubGroupsClient().update4(subgroupId, requestData);
        } else {
            const requestData: SubGroupRequestDTO = { name, organizationId };
            const newSubgroup = await new SubGroupsClient().create3(requestData);
            history.push(`/organizations/${organizationId}/subgroups/${newSubgroup.externalId}`);
        }
        dispatch(snackbar.addMessage({ title: "Successfully updated", message: `${name} details were saved`, alertType: 'success' }))
    }
    catch (error) {
        dispatch(snackbar.addError(error, undefined, 'Error writing to the database'));
    }
}

async function deleteSubgroup(subgroupId: string, organizationId: string, dispatch: Dispatch<any>, history: History<any>) {
    try {
        await new SubGroupsClient().delete3(subgroupId);
        history.push(`/organizations/${organizationId}`);
    }
    catch (error) {
        dispatch(snackbar.addError(error, undefined, 'Error writing to the database'));
    }
}

export default function SubgroupForm(props: SubgroupProps) {
    const organizationId = 'organizationId' in props ? props.organizationId : props.match.params.organizationId;
    const subgroupId = 'subgroupId' in props ? props.subgroupId : props.match.params.subgroupId;
    const history = useHistory()
    const dispatch = useDispatch()
    const [subgroup, setSubgroup] = useState<SubGroupResponseDTO | 'Loading' | undefined>('Loading');
    const [orgName, setOrgName] = useState<string>("");
    const isAdmin = useIsUserInRoles(Admin)

    useEffect(() => {
        const fetchData = async () => {
            if (subgroupId) {
                const subgroup = await new SubGroupsClient().get5(subgroupId);
                setSubgroup(subgroup);
            }
            else
                setSubgroup(undefined)
        }
        fetchData();
    }, [organizationId, subgroupId])

    useEffect(() => {
        const fetchData = async () => {
            const selectedOrg = await new OrganizationsClient().get3(organizationId);
            setOrgName(selectedOrg.name);
        }
        fetchData();
    }, [organizationId, subgroupId])


    if (subgroup === 'Loading')
        return <CenteredProgress />
    else
        return (
            <>
                <Formik initialValues={{ name: subgroup?.name ?? '' } as SubgroupFormProperties}
                    validationSchema={SubgroupDetailsFormSchema}
                    onSubmit={async (values, _) => formSubmitCallback(values.name, subgroupId, organizationId, dispatch, history)}>
                    {/* supply a function to formik so that we can access these props*/}
                    {({ submitForm, isSubmitting, touched }) => (
                        <Grid container>
                            <Grid item sm={10}>
                                <Form translate='yes'>
                                    <Grid container alignItems='center' spacing={1}>
                                        <Grid item>
                                            <Typography paragraph display='inline'><b>Subgroup Name:</b></Typography>
                                        </Grid>
                                        <Grid item>
                                            {/* pass in a material ui component to the Formik <Field /> component */}
                                            {isAdmin ?
                                                <Field component={TextField} name='name' type='text' />
                                                : <Typography>{subgroup?.name ?? ''}</Typography>
                                            }

                                        </Grid>
                                    </Grid>
                                </Form>
                                <Grid item>
                                    <Typography paragraph display='inline'><b>Organization Name:</b> {orgName}</Typography>
                                </Grid>
                                {subgroup &&
                                    <>
                                        <Grid container alignItems='center' spacing={1}>
                                            <Grid item>
                                                <Typography paragraph display='inline'><b>Created Date:</b></Typography>
                                            </Grid>
                                            <Grid item>
                                                <Typography paragraph display='inline'>{utcToLocal(subgroup.created).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)}</Typography>
                                            </Grid>
                                        </Grid>
                                        <Grid container alignItems='center' spacing={1}>
                                            <Grid item>
                                                <Typography paragraph display='inline'><b>Last Updated:</b></Typography>
                                            </Grid>
                                            <Grid item>
                                                <Typography paragraph display='inline'>{utcToLocal(subgroup.lastUpdated).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)}</Typography>
                                            </Grid>
                                        </Grid>
                                    </>}
                            </Grid>
                            {isAdmin &&
                                <Grid container item sm={2} direction='column' spacing={2} alignItems='center'>
                                    <Grid item>
                                        <Button variant='contained' color='primary' size='medium' disabled={isSubmitting} onClick={submitForm}>Save Changes</Button>
                                    </Grid>
                                    {subgroup &&
                                        <Grid item>
                                            <Button variant='contained' color='primary' size='medium' onClick={d => deleteSubgroup(subgroup.externalId, organizationId, dispatch, history)}>Delete</Button>
                                        </Grid>}
                                </Grid>}
                        </Grid>
                    )}
                </Formik>
            </>
        )
}